import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const RECORD_CAP_MS = 10 * 60 * 1000;

// Probed in order; the first the browser will write wins. Chromium and Firefox
// write WebM/Opus, Safari writes MP4/AAC. Each container maps to the content
// type and extension the clip goes up under, which the API accepts as-is.
const CONTAINERS: readonly { mimeType: string; type: string; ext: string }[] = [
  { mimeType: "audio/webm;codecs=opus", type: "audio/webm", ext: "webm" },
  { mimeType: "audio/webm", type: "audio/webm", ext: "webm" },
  { mimeType: "audio/mp4", type: "audio/mp4", ext: "m4a" },
];

type Container = (typeof CONTAINERS)[number];

function supportedContainer() {
  if (typeof MediaRecorder === "undefined" || !("mediaDevices" in navigator)) {
    return undefined;
  }
  return CONTAINERS.find((c) => MediaRecorder.isTypeSupported(c.mimeType));
}

function stopTracks(stream: { getTracks: () => { stop: () => void }[] }) {
  for (const track of stream.getTracks()) track.stop();
}

/**
 * The take's name: when it was spoken, not what it technically is.
 *
 * "Narration 28 Aug 14.32" tells two takes apart at a glance and reads like a
 * thing the attorney made; a literal "recording.webm" named every take the
 * same and looked like a temp file. Dots rather than a colon in the time, so
 * the name survives every filesystem it may be saved onto.
 */
function takeName(spoken: Date, ext: string): string {
  const day = spoken.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
  const hours = String(spoken.getHours()).padStart(2, "0");
  const minutes = String(spoken.getMinutes()).padStart(2, "0");
  return `Narration ${day} ${hours}.${minutes}.${ext}`;
}

function clipFile(chunks: Blob[], container: Container): File | undefined {
  if (chunks.length === 0) return undefined;
  const blob = new Blob(chunks, { type: container.type });
  return new File([blob], takeName(new Date(), container.ext), {
    type: container.type,
  });
}

/**
 * A recorder over the stream that collects its own chunks and, on stop, hands
 * the finished clip on (or says nothing was captured) before releasing. A
 * capture that has been superseded only releases: its clip has no taker.
 */
function captureRecorder(
  stream: MediaStream,
  container: Container,
  finish: {
    live: () => boolean;
    onClip: (file: File) => void;
    release: () => void;
  },
): MediaRecorder {
  const recorder = new MediaRecorder(stream, { mimeType: container.mimeType });
  const chunks: Blob[] = [];
  recorder.ondataavailable = (event) => {
    if (event.data.size > 0) chunks.push(event.data);
  };
  recorder.onstop = () => {
    if (finish.live()) {
      const clip = clipFile(chunks, container);
      if (clip === undefined) {
        toast.error("Nothing was captured. Try recording again.");
      } else {
        finish.onClip(clip);
      }
    }
    finish.release();
  };
  return recorder;
}

/**
 * Capture microphone audio and hand the finished take over as a File named
 * `recording.<ext>`, in whichever container the browser can write.
 *
 * A generation counter drops in-flight getUserMedia when the owner unmounts or
 * a new start supersedes it, so a permission grant cannot open the mic after
 * the user has moved on from the surface that asked.
 */
export function useAudioRecorder(onClip: (file: File) => void) {
  const [recording, setRecording] = useState(false);
  // The live stream, surfaced so a level meter can listen alongside the
  // recorder. Present exactly while a capture runs.
  const [stream, setStream] = useState<MediaStream | null>(null);
  const genRef = useRef(0);
  const busyRef = useRef(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const capRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const onClipRef = useRef(onClip);
  onClipRef.current = onClip;

  const release = useCallback(() => {
    const stream = streamRef.current;
    if (stream !== null) {
      stopTracks(stream);
      streamRef.current = null;
    }
    recorderRef.current = null;
    busyRef.current = false;
    setStream(null);
    if (capRef.current !== null) {
      clearTimeout(capRef.current);
      capRef.current = null;
    }
    setRecording(false);
  }, []);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.stop();
    }
  }, []);

  // Leaving mid-capture stops the recorder and lets the clip finish its
  // ordinary stop path: the hand-off closes over the callback the capture
  // started under, so the audio lands where it was being given rather than
  // being dropped. Only a grant still in flight is discarded.
  const abort = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder !== null && recorder.state !== "inactive") {
      recorder.stop();
      return;
    }
    genRef.current += 1;
    const stream = streamRef.current;
    if (stream !== null) stopTracks(stream);
    release();
  }, [release]);

  const start = useCallback(async () => {
    if (busyRef.current) return;
    const container = supportedContainer();
    if (container === undefined) {
      toast.error("This browser cannot record audio.");
      return;
    }
    busyRef.current = true;
    const gen = ++genRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (gen !== genRef.current) {
        stopTracks(stream);
        return;
      }
      streamRef.current = stream;
      setStream(stream);
      recorderRef.current = captureRecorder(stream, container, {
        live: () => gen === genRef.current,
        onClip: (file) => {
          onClipRef.current(file);
        },
        release,
      });
      recorderRef.current.start();
      setRecording(true);
      capRef.current = setTimeout(stop, RECORD_CAP_MS);
    } catch {
      if (gen !== genRef.current) return;
      toast.error(
        "Could not start a recording. Check the microphone permission.",
      );
      release();
    }
  }, [stop, release]);

  useEffect(() => () => abort(), [abort]);

  return { recording, stream, start, stop };
}
